/**
 * CharterGroupPage — standalone route host for the Phase-2/3 group session.
 *
 * Owns only what a host owns: the sidebar shell, the WebSocket lifecycle, the
 * REST fetches, and the navigation guards. The chat itself is
 * `CharterGroupBody` and the operations panel is `CharterGroupSidebar` — the
 * same components the in-game shell (`components/Student/ChatRoom.tsx`)
 * renders, so there is exactly one implementation of the group chat.
 *
 * The Prep Coach tab shows the student's completed Phase-1 session read-only,
 * mirroring how the in-game shell swaps bodies per tab.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { AlertCircle, Loader2, Sparkles, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { SidebarProvider } from '@/components/ui/sidebar'
import OGESidebar from '@/components/OGE/Sidebar/OGESidebar'
import OGESidebarHeader from '@/components/OGE/Sidebar/OGESidebarHeader'
import Navbar from '@/components/Layout/Navbar'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { getAccessToken } from '@/utils/authUtils'
import ROUTES from '@/routes/routes'

import {
  fetchCharterSession,
  fetchMyProfile,
  fetchMyTeamSummaries,
  fetchPrepHistory,
} from '../../redux/asyncThunks/charterThunks'
import {
  connectCharterGroupWS,
  disconnectCharterGroupWS,
} from '../../redux/asyncThunks/charterWsThunks'
import {
  selectCharterSession,
  selectGroupIsConnected,
  selectRenegotiationMode,
} from '../../redux/selectors/charterSelectors'
import { CharterCollaborationPanel } from '../../components/Shared/CharterCollaborationPanel'
import { CharterGroupBody } from '../../components/Shell/CharterGroupBody'
import { CharterGroupSidebar } from '../../components/Shell/CharterGroupSidebar'
import { CharterPrepBody } from '../../components/Shell/CharterPrepBody'
import { CharterPrepSidebar } from '../../components/Shell/CharterPrepSidebar'

export default function CharterGroupPage() {
  const { roomId = '' } = useParams<{ roomId: string }>()
  const [searchParams] = useSearchParams()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const session = useAppSelector(selectCharterSession)
  const isConnected = useAppSelector(selectGroupIsConnected)
  const renegotiationMode = useAppSelector(selectRenegotiationMode)

  const currentUser = useAppSelector((s) => s.user.user)
  const userName =
    currentUser?.name || currentUser?.email?.split('@')[0] || 'You'

  const [activeAgent, setActiveAgent] = useState<'prep' | 'group'>('group')

  const inviteCode = searchParams.get('inviteCode') ?? ''
  const teamId = searchParams.get('teamId') ?? ''
  const monitor = searchParams.get('monitor') === 'true'

  useEffect(() => {
    dispatch(fetchCharterSession({ roomId }))
    dispatch(fetchMyProfile({ roomId }))
    dispatch(fetchMyTeamSummaries({ roomId }))
    dispatch(fetchPrepHistory({ roomId }))
  }, [dispatch, roomId])

  // If the professor moved this team back to Phase 1 (Previous Phase) or reset
  // Round 0, the session reverts to phase1_prep — send students to the prep page.
  useEffect(() => {
    if (monitor) return
    if (session?.phase === 'phase1_prep') {
      navigate(
        ROUTES.STUDENT.CHARTER_PREP(roomId) +
          `?inviteCode=${inviteCode}&teamId=${teamId}`
      )
    }
  }, [session?.phase, monitor, navigate, roomId, inviteCode, teamId])

  // Connect WS on mount.
  useEffect(() => {
    const token = getAccessToken()
    if (!token || !roomId || !inviteCode || !teamId) return
    dispatch(
      connectCharterGroupWS({
        jwtToken: token,
        roomId,
        inviteCode,
        teamId,
        monitor,
      })
    )
    return () => {
      dispatch(disconnectCharterGroupWS())
    }
  }, [dispatch, roomId, inviteCode, teamId, monitor])

  // ── Guards ──────────────────────────────────────────────────────────────
  if (
    session &&
    session.phase === 'phase1_prep' &&
    session.status !== 'in_progress'
  ) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='flex items-center gap-2 text-amber-600'>
          <AlertCircle className='h-5 w-5' />
          <p className='text-sm'>
            Phase 1 prep must be completed by all team members first.
          </p>
        </div>
      </div>
    )
  }
  if (!inviteCode || !teamId) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <div className='flex items-center gap-2 text-red-600'>
          <AlertCircle className='h-5 w-5' />
          <p className='text-sm'>
            Missing session parameters. Please rejoin from the waiting room.
          </p>
        </div>
      </div>
    )
  }

  const isPrepTab = activeAgent === 'prep'

  return (
    <SidebarProvider className='h-screen'>
      <OGESidebar
        header={
          <OGESidebarHeader
            userName={userName}
            avatarSrc={currentUser?.profile_picture || ''}
            subText={`Group Session — Phase 2${monitor ? ' (Observer)' : ''}`}
          />
        }
        content={
          isPrepTab ? (
            <CharterPrepSidebar />
          ) : (
            <CharterGroupSidebar roomId={roomId} monitor={monitor} />
          )
        }
        chatContent={
          <CharterCollaborationPanel
            type='group'
            isConnected={isConnected}
            activeAgent={activeAgent}
            onAgentChange={setActiveAgent}
          />
        }
        initialPanel='game'
      />

      <div className='flex flex-col w-full h-full overflow-auto'>
        {/* Phase info bar */}
        <div className='sticky top-0 z-10 px-14 py-2 ml-[2.5rem]'>
          <div className='flex items-center justify-between p-3 bg-white rounded-md shadow'>
            <div className='flex items-center gap-2'>
              {isPrepTab ? (
                <Sparkles className='h-5 w-5 text-indigo-500' />
              ) : (
                <Users className='h-5 w-5 text-indigo-500' />
              )}
              <div>
                <h3 className='text-sm font-semibold text-dark-blue flex items-center gap-2'>
                  {isPrepTab ? 'Individual Prep' : 'Group Charter Session'}
                  {!isPrepTab && renegotiationMode && (
                    <Badge className='bg-amber-100 text-amber-700 border-amber-200 text-xs'>
                      Re-negotiating
                    </Badge>
                  )}
                </h3>
                <p className='text-xs text-gray-500'>
                  {isPrepTab
                    ? 'Phase 1 — Your preparation session history'
                    : monitor
                      ? 'Observer mode — read only'
                      : 'Phase 2 — Collaborate to build your charter'}
                </p>
              </div>
            </div>
            {!isPrepTab && !isConnected && (
              <Badge
                variant='outline'
                className='text-xs text-amber-600 border-amber-300'
              >
                <Loader2 className='h-3 w-3 mr-1 animate-spin' />
                Connecting…
              </Badge>
            )}
          </div>
        </div>

        <div className='flex-1 min-h-0 flex flex-col'>
          <div className='ms-6'>
            <Navbar />
          </div>

          <div className='flex overflow-x-hidden flex-col flex-1 w-full min-w-0 min-h-0'>
            {isPrepTab ? (
              <CharterPrepBody readOnly />
            ) : (
              <CharterGroupBody roomId={roomId} monitor={monitor} />
            )}
          </div>
        </div>
      </div>
    </SidebarProvider>
  )
}
